import React, { useState } from 'react';
import { uploadImage } from '../../services/upload.service';
import * as adminService from '../../services/admin.service';
import ImageLinkInput from '../common/ImageLinkInput';

/**
 * Drag-and-drop image setter for a Hub event — shared by every event with
 * the same title (the de-facto "type"), shown on upcoming events before any
 * replay exists. If this specific occurrence already has a recorded replay,
 * the same image also becomes that replay's real Bunny thumbnail — one
 * upload, both places. Rectangle ~1280x720 recommended (Oct 29: thumbnails are rectangles now), but anything works —
 * displayed with object-cover everywhere it appears.
 */
export default function EventImageDropzone({ eventId, eventTitle, onSaved, onError, onCancel }) {
  const [altDraft, setAltDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      onError?.("That file isn't an image — try a JPEG, PNG, GIF, or WEBP.");
      return;
    }
    setSaving(true);
    try {
      await saveUrl(await uploadImage(file));
    } catch (err) {
      onError?.(err.response?.data?.error || 'Could not set that image.');
    } finally {
      setSaving(false);
    }
  };

  // Oct 29 (Jenn): upload AND paste a link, in every picture box.
  const saveUrl = async (imageUrl) => {
    await adminService.setHubEventImage(eventId, { imageUrl, altText: altDraft.trim() });
    onSaved?.();
  };
  const handleLink = async (url) => {
    setSaving(true);
    try {
      await saveUrl(url);
    } catch (err) {
      onError?.(err.response?.data?.error || 'Could not set that image.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-4 px-2 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-reset-terracotta'}`}
      >
        <span className="text-xs font-medium text-gray-500">
          {saving ? 'Uploading…' : 'Drop a rectangle image here (like 1280×720), or click to choose'}
        </span>
        <span className="text-[10px] text-gray-400">Used for every "{eventTitle}" going forward — not just this date</span>
        <input type="file" accept="image/*" className="hidden" disabled={saving}
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </label>
      <ImageLinkInput onLink={handleLink} disabled={saving} />
      <input value={altDraft} onChange={e => setAltDraft(e.target.value)}
        placeholder="Alt text (what's in the image, for accessibility)"
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      {onCancel && (
        <div className="flex justify-end">
          <button onClick={onCancel} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
        </div>
      )}
    </div>
  );
}
